import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase.ts';
import PlayerName, { useChristmasEffects } from '../Common/PlayerName.tsx';
import './ChristmasLeaderboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';

interface LeaderboardEntry {
  user_id: string;
  user_name: string;
  days_completed: number;
  total_score: number;
  total_time: number;
  hasChristmasEffect: boolean;
  progress: number;
  last_completion?: string;
}

// Fonction pour formater le temps
const formatTime = (seconds: number): string => {
  if (!seconds || isNaN(seconds)) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
};

// Fonction pour obtenir le titre selon la progression
const getProgressTitle = (days: number): { title: string; emoji: string; color: string } => {
  if (days >= 24) return { title: 'Maître de l\'Avent', emoji: '👑', color: '#ffd700' };
  if (days >= 20) return { title: 'Expert Festif', emoji: '⭐', color: '#ff6b6b' };
  if (days >= 15) return { title: 'Enthousiaste', emoji: '🎄', color: '#6bcb77' };
  if (days >= 10) return { title: 'Découvreur', emoji: '🎁', color: '#4d96ff' };
  if (days >= 5) return { title: 'Débutant', emoji: '🌟', color: '#a855f7' };
  return { title: 'Novice', emoji: '❄️', color: '#94a3b8' };
};

const ChristmasLeaderboard: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
      if (!u) navigate('/login');
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    async function fetchLeaderboard() {
      if (!user) return;
      setLoadingData(true);
      
      try {
        const token = await user.getIdToken();
        const res = await fetch(`${API_URL}/api/christmas/leaderboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setLeaderboard(data.leaderboard || []);
      } catch (error) {
        console.error('Erreur lors du chargement du classement:', error);
      } finally {
        setLoadingData(false);
      }
    }
    fetchLeaderboard();
  }, [user]);

  // Charger les effets de Noël
  const userIds = leaderboard.map(r => r.user_id);
  const { effects: christmasEffects } = useChristmasEffects(userIds);

  if (loading) return <div className="xmas-lb-loading">Chargement…</div>;
  if (!user) return null;

  // Calculer les jours restants jusqu'au 25 décembre
  const now = new Date();
  const christmas = new Date(now.getFullYear(), 11, 25);
  const daysUntilChristmas = Math.max(0, Math.ceil((christmas.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));

  return (
    <div className="xmas-lb-container">
      {/* Particules de neige */}
      <div className="xmas-lb-snow">
        {[...Array(30)].map((_, i) => (
          <div key={i} className="snowflake" style={{
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 10}s`,
            animationDuration: `${8 + Math.random() * 10}s`
          }}>❄</div>
        ))}
      </div>

      {/* Header */}
      <header className="xmas-lb-header">
        <div className="xmas-lb-title-wrapper">
          <div className="xmas-lb-ornament left">🎄</div>
          <h1 className="xmas-lb-title">
            <span className="title-line">Classement</span>
            <span className="title-line accent">de l'Avent 2025</span>
          </h1>
          <div className="xmas-lb-ornament right">🎄</div>
        </div>
        
        {daysUntilChristmas > 0 && (
          <div className="xmas-lb-countdown">
            <span className="countdown-icon">🎅</span>
            <span className="countdown-text">
              Plus que <strong>{daysUntilChristmas}</strong> jour{daysUntilChristmas > 1 ? 's' : ''} avant Noël !
            </span>
          </div>
        )}

        <nav className="xmas-lb-nav">
          <button onClick={() => navigate('/christmas')} className="xmas-lb-btn primary">
            <span className="btn-icon">📅</span>
            <span>Calendrier</span>
          </button>
          <button onClick={() => navigate('/')} className="xmas-lb-btn">
            <span className="btn-icon">🏠</span>
            <span>Accueil</span>
          </button>
        </nav>
      </header>

      {/* Contenu principal */}
      <main className="xmas-lb-main">
        {loadingData ? (
          <div className="xmas-lb-loading-state">
            <div className="loading-gift">🎁</div>
            <p>Ouverture des cadeaux...</p>
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="xmas-lb-empty">
            <div className="empty-scene">
              <span className="empty-santa">🎅</span>
              <span className="empty-tree">🎄</span>
              <span className="empty-gift">🎁</span>
            </div>
            <h2>Le traîneau est encore vide !</h2>
            <p>Soyez le premier à participer au calendrier de l'Avent.</p>
            <button onClick={() => navigate('/christmas')} className="xmas-lb-btn primary large">
              Commencer l'aventure 🚀
            </button>
          </div>
        ) : (
          <>
            {/* Podium Top 3 */}
            <section className="xmas-lb-podium-section">
              <h2 className="section-title">
                <span>🏆</span> Les Champions de Noël <span>🏆</span>
              </h2>
              
              <div className="xmas-lb-podium">
                {/* 2ème place */}
                {leaderboard[1] && (
                  <div className="podium-slot silver">
                    <div className="podium-card">
                      <div className="podium-glow"></div>
                      <div className="podium-medal">🥈</div>
                      <div className="podium-avatar-wrapper">
                        <div className="podium-avatar">
                          {leaderboard[1].hasChristmasEffect ? '❄️' : '🦌'}
                        </div>
                      </div>
                      <div className="podium-name">
                        <PlayerName 
                          userId={leaderboard[1].user_id}
                          userName={leaderboard[1].user_name || 'Joueur'}
                          hasChristmasEffect={leaderboard[1].hasChristmasEffect || christmasEffects[leaderboard[1].user_id]}
                        />
                      </div>
                      <div className="podium-title" style={{ color: getProgressTitle(leaderboard[1].days_completed).color }}>
                        {getProgressTitle(leaderboard[1].days_completed).emoji} {getProgressTitle(leaderboard[1].days_completed).title}
                      </div>
                      <div className="podium-stats-grid">
                        <div className="podium-stat">
                          <span className="stat-value">{leaderboard[1].days_completed}</span>
                          <span className="stat-label">jours</span>
                        </div>
                        <div className="podium-stat">
                          <span className="stat-value">{leaderboard[1].total_score || 0}</span>
                          <span className="stat-label">points</span>
                        </div>
                      </div>
                    </div>
                    <div className="podium-base">2</div>
                  </div>
                )}

                {/* 1ère place */}
                {leaderboard[0] && (
                  <div className="podium-slot gold">
                    <div className="podium-card winner">
                      <div className="podium-glow"></div>
                      <div className="podium-crown">👑</div>
                      <div className="podium-medal">🥇</div>
                      <div className="podium-avatar-wrapper">
                        <div className="podium-avatar">
                          {leaderboard[0].hasChristmasEffect ? '❄️' : '🎅'}
                        </div>
                        {leaderboard[0].hasChristmasEffect && (
                          <div className="effect-sparkles">✨</div>
                        )}
                      </div>
                      <div className="podium-name">
                        <PlayerName 
                          userId={leaderboard[0].user_id}
                          userName={leaderboard[0].user_name || 'Joueur'}
                          hasChristmasEffect={leaderboard[0].hasChristmasEffect || christmasEffects[leaderboard[0].user_id]}
                        />
                      </div>
                      <div className="podium-title" style={{ color: getProgressTitle(leaderboard[0].days_completed).color }}>
                        {getProgressTitle(leaderboard[0].days_completed).emoji} {getProgressTitle(leaderboard[0].days_completed).title}
                      </div>
                      <div className="podium-stats-grid">
                        <div className="podium-stat">
                          <span className="stat-value">{leaderboard[0].days_completed}</span>
                          <span className="stat-label">jours</span>
                        </div>
                        <div className="podium-stat">
                          <span className="stat-value">{leaderboard[0].total_score || 0}</span>
                          <span className="stat-label">points</span>
                        </div>
                      </div>
                      {leaderboard[0].hasChristmasEffect && (
                        <div className="winner-badge">
                          <span>❄️ Effet débloqué</span>
                        </div>
                      )}
                    </div>
                    <div className="podium-base">1</div>
                  </div>
                )}

                {/* 3ème place */}
                {leaderboard[2] && (
                  <div className="podium-slot bronze">
                    <div className="podium-card">
                      <div className="podium-glow"></div>
                      <div className="podium-medal">🥉</div>
                      <div className="podium-avatar-wrapper">
                        <div className="podium-avatar">
                          {leaderboard[2].hasChristmasEffect ? '❄️' : '⛄'}
                        </div>
                      </div>
                      <div className="podium-name">
                        <PlayerName 
                          userId={leaderboard[2].user_id}
                          userName={leaderboard[2].user_name || 'Joueur'}
                          hasChristmasEffect={leaderboard[2].hasChristmasEffect || christmasEffects[leaderboard[2].user_id]}
                        />
                      </div>
                      <div className="podium-title" style={{ color: getProgressTitle(leaderboard[2].days_completed).color }}>
                        {getProgressTitle(leaderboard[2].days_completed).emoji} {getProgressTitle(leaderboard[2].days_completed).title}
                      </div>
                      <div className="podium-stats-grid">
                        <div className="podium-stat">
                          <span className="stat-value">{leaderboard[2].days_completed}</span>
                          <span className="stat-label">jours</span>
                        </div>
                        <div className="podium-stat">
                          <span className="stat-value">{leaderboard[2].total_score || 0}</span>
                          <span className="stat-label">points</span>
                        </div>
                      </div>
                    </div>
                    <div className="podium-base">3</div>
                  </div>
                )}
              </div>
            </section>

            {/* Reste du classement */}
            {leaderboard.length > 3 && (
              <section className="xmas-lb-list-section">
                <h2 className="section-title">
                  <span>🎄</span> Autres Participants <span>🎄</span>
                </h2>
                
                <div className="xmas-lb-list">
                  {leaderboard.slice(3).map((entry, i) => {
                    const progressInfo = getProgressTitle(entry.days_completed);
                    const progressPercent = (entry.days_completed / 24) * 100;
                    
                    return (
                      <div 
                        key={entry.user_id} 
                        className={`xmas-lb-list-item ${entry.hasChristmasEffect ? 'has-effect' : ''}`}
                      >
                        <div className="list-rank">
                          <span className="rank-number">{i + 4}</span>
                        </div>
                        
                        <div className="list-avatar">
                          {progressInfo.emoji}
                        </div>
                        
                        <div className="list-info">
                          <div className="list-name-row">
                            <PlayerName 
                              userId={entry.user_id}
                              userName={entry.user_name || 'Joueur'}
                              hasChristmasEffect={entry.hasChristmasEffect || christmasEffects[entry.user_id]}
                            />
                            {entry.hasChristmasEffect && <span className="mini-badge">❄️</span>}
                          </div>
                          <div className="list-title" style={{ color: progressInfo.color }}>
                            {progressInfo.title}
                          </div>
                          <div className="list-progress">
                            <div className="progress-bar">
                              <div 
                                className="progress-fill" 
                                style={{ width: `${progressPercent}%` }}
                              ></div>
                            </div>
                            <span className="progress-text">{entry.days_completed}/24</span>
                          </div>
                        </div>
                        
                        <div className="list-stats">
                          <div className="list-stat">
                            <span className="stat-icon">⭐</span>
                            <span className="stat-val">{entry.total_score || 0}</span>
                          </div>
                          <div className="list-stat">
                            <span className="stat-icon">⏱️</span>
                            <span className="stat-val">{formatTime(entry.total_time)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}

        {/* Légende / Info */}
        <section className="xmas-lb-info">
          <div className="info-card">
            <div className="info-icon">🎁</div>
            <div className="info-content">
              <h3>Comment débloquer l'effet ❄️ ?</h3>
              <p>
                Complétez les <strong>24 jours</strong> du calendrier de l'Avent 
                pour débloquer l'effet "Flocon de Neige" sur votre pseudo !
              </p>
            </div>
          </div>
          
          <div className="info-titles">
            <h4>Titres de progression</h4>
            <div className="titles-grid">
              <div className="title-item"><span>❄️</span> Novice (1-4j)</div>
              <div className="title-item"><span>🌟</span> Débutant (5-9j)</div>
              <div className="title-item"><span>🎁</span> Découvreur (10-14j)</div>
              <div className="title-item"><span>🎄</span> Enthousiaste (15-19j)</div>
              <div className="title-item"><span>⭐</span> Expert (20-23j)</div>
              <div className="title-item"><span>👑</span> Maître (24j)</div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ChristmasLeaderboard;
